import { FileText, Flag, Loader2, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { ResearchRoom } from '@repo/contracts';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/popover';
import { Tooltip } from '@repo/ui/tooltip';

import { trackEvent } from '../analytics/posthog';
import { DeckButton } from '../research-report/deck-button';
import {
  reportPath,
  useReport,
  useRewriteReport,
} from '../research-report/use-report';

/**
 * "End session", with a moment to be sure: ending closes the room to new
 * questions and has Floto write the final report, so it is not a click to
 * make by accident.
 *
 * Shown from the start, so it is never a thing to hunt for; until the room
 * has answered a question it is greyed out with `notYet` saying why — the
 * report is built from what people said, and a poll alone gives it nothing
 * to quote.
 */
export function EndSessionButton({
  compact = false,
  disabled,
  ending,
  notYet,
  onEnd,
}: {
  /** Icon only, for the phone layout's top bar. */
  compact?: boolean;
  disabled: boolean;
  ending: boolean;
  notYet?: string;
  onEnd: () => void;
}) {
  const [open, setOpen] = useState(false);
  const iconClass = compact
    ? 'flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-button text-text-secondary transition active:bg-surface-hover disabled:cursor-default disabled:opacity-40'
    : null;
  if (notYet && compact) {
    // No hover on a phone to hang the hint off: it reads as not yet.
    return (
      <button
        aria-label={`End session — ${notYet}`}
        className={iconClass ?? undefined}
        disabled
        type="button"
      >
        <Flag aria-hidden="true" className="size-4.5" />
      </button>
    );
  }
  if (notYet) {
    // A disabled button takes no hover, so the hint hangs off a wrapper and
    // the button lets the pointer through to it.
    return (
      <Tooltip label={notYet} side="bottom">
        <span className="shrink-0" tabIndex={0}>
          <button
            className="flex h-9 items-center gap-2 rounded-button border border-text-secondary bg-surface px-3.5 text-ui font-semibold text-text-secondary opacity-50 pointer-events-none"
            disabled
            type="button"
          >
            <Flag aria-hidden="true" className="size-4" />
            End session
          </button>
        </span>
      </Tooltip>
    );
  }
  return (
    <Popover
      onOpenChange={(next) => {
        if (next) trackEvent('room_end_opened');
        setOpen(next);
      }}
      open={open}
    >
      <PopoverTrigger
        aria-label={compact ? (ending ? 'Ending…' : 'End session') : undefined}
        className={
          iconClass ??
          'flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-button border border-text-secondary bg-surface px-3.5 text-ui font-semibold text-text-secondary transition hover:border-text-primary hover:text-text-primary active:scale-[0.98] disabled:cursor-default disabled:opacity-50'
        }
        disabled={disabled || ending}
      >
        {ending ? (
          <Loader2
            aria-hidden="true"
            className={`${compact ? 'size-4.5' : 'size-4'} animate-spin`}
          />
        ) : (
          <Flag
            aria-hidden="true"
            className={compact ? 'size-4.5' : 'size-4'}
          />
        )}
        {compact ? null : ending ? 'Ending…' : 'End session'}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 rounded-card border-hairline bg-surface p-4 shadow-popover"
        side="bottom"
      >
        <p className="text-ui-strong font-semibold text-text-primary">
          End this session?
        </p>
        <p className="mt-1 text-caption leading-relaxed text-text-secondary">
          Floto writes the final research report — findings, every participant’s
          take, what to do next. The room stops taking questions.
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <button
            className="h-8 cursor-pointer rounded-button px-3 text-ui font-medium text-text-secondary transition hover:bg-surface-hover hover:text-text-primary"
            onClick={() => {
              trackEvent('room_end_cancelled');
              setOpen(false);
            }}
            type="button"
          >
            Keep going
          </button>
          <button
            className="flex h-8 cursor-pointer items-center gap-1.5 rounded-button bg-brand px-3 text-ui font-semibold text-white transition hover:bg-brand-alt"
            onClick={() => {
              setOpen(false);
              onEnd();
            }}
            type="button"
          >
            <FileText aria-hidden="true" className="size-3.5" />
            End & write report
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Where the composer was, once the session is over: the report being
 * written, ready to open, or failed with a way to try again.
 */
export function SessionEndedBar({ room }: { room: ResearchRoom }) {
  const { data } = useReport(room.id);
  const rewrite = useRewriteReport(room.id);
  const status = rewrite.isPending ? 'pending' : data?.report?.status;
  const ended = room.endedAt
    ? new Date(room.endedAt).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  return (
    <div className="flex flex-col gap-3 rounded-card bg-warm px-3.5 py-3">
      <div className="flex items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-button bg-surface text-text-primary ring-1 ring-hairline">
          {status === 'pending' ? (
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          ) : (
            <Flag aria-hidden="true" className="size-4" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-ui-strong font-semibold text-text-primary">
            {status === 'pending'
              ? 'Floto is writing your report…'
              : status === 'failed'
                ? 'The report could not be written'
                : 'Session ended'}
          </p>
          <p className="truncate text-caption text-text-muted">
            {status === 'pending'
              ? 'Checking every quote against the transcript'
              : ended
                ? `Ended ${ended}`
                : 'This room takes no more questions'}
          </p>
        </div>
      </div>
      {status === 'failed' ? (
        <button
          className="flex h-8 cursor-pointer items-center justify-center gap-1.5 self-start rounded-button bg-surface px-3 text-ui font-semibold text-text-primary ring-1 ring-hairline transition hover:bg-surface-hover"
          onClick={() => {
            trackEvent('report_rewrite_requested', {
              room_id: room.id,
              source: 'room',
            });
            rewrite.mutate();
          }}
          type="button"
        >
          <RotateCcw aria-hidden="true" className="size-3.5" />
          Try again
        </button>
      ) : status === 'done' ? (
        <div className="flex gap-2">
          <DeckButton
            className="flex-1 justify-center bg-surface"
            roomId={room.id}
            tone="quiet"
          />
          <Link
            className="flex h-8 flex-1 items-center justify-center gap-1.5 rounded-button bg-brand px-3 text-ui font-semibold text-white transition hover:bg-brand-alt"
            state={{ from: 'room' }}
            to={reportPath(room)}
          >
            <FileText aria-hidden="true" className="size-3.5" />
            Open report
          </Link>
        </div>
      ) : null}
    </div>
  );
}
